/**
 * Slot — standalone polymorphic render utility.
 *
 * Adapted from @radix-ui/react-slot (MIT licence).
 * Original: https://github.com/radix-ui/primitives/blob/main/packages/react/slot/src/slot.tsx
 *
 * Behaviour:
 *   - Merges className (concatenation), style (shallow merge), and event handlers (both called)
 *   - Forwards refs via composeRefs
 *   - Supports the Slottable pattern for nested slot control
 *   - Handles React.lazy components via React.use
 *
 * This module eliminates the need for `radix-ui` (the barrel) or `@radix-ui/react-slot`
 * in any component that only uses Slot (i.e. the `asChild` pattern).
 */

import * as React from 'react'

// ---------------------------------------------------------------------------
// composeRefs — inlined from @radix-ui/react-compose-refs (MIT licence)
// ---------------------------------------------------------------------------

type PossibleRef<T> = React.Ref<T> | undefined

function setRef<T>(ref: PossibleRef<T>, value: T): ReturnType<React.RefCallback<T>> {
  if (typeof ref === 'function') {
    return ref(value)
  } else if (ref !== null && ref !== undefined) {
    ;(ref as React.MutableRefObject<T>).current = value
  }
}

function composeRefs<T>(...refs: PossibleRef<T>[]): React.RefCallback<T> {
  return (node) => {
    let hasCleanup = false
    const cleanups = refs.map((ref) => {
      const cleanup = setRef(ref, node as T)
      if (!hasCleanup && typeof cleanup === 'function') {
        hasCleanup = true
      }
      return cleanup
    })

    if (hasCleanup) {
      return () => {
        for (let i = 0; i < cleanups.length; i++) {
          const cleanup = cleanups[i]
          if (typeof cleanup === 'function') {
            cleanup()
          } else {
            setRef(refs[i], null as T)
          }
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Slottable
// ---------------------------------------------------------------------------

const SLOTTABLE_IDENTIFIER = Symbol('elemental.slottable')

interface SlottableProps {
  children: React.ReactNode
}

interface SlottableComponent extends React.FC<SlottableProps> {
  __slotId: symbol
}

const Slottable: SlottableComponent = ({ children }) => {
  return <>{children}</>
}
Slottable.__slotId = SLOTTABLE_IDENTIFIER

function isSlottable(child: React.ReactNode): child is React.ReactElement<SlottableProps, SlottableComponent> {
  return (
    React.isValidElement(child) &&
    typeof child.type === 'function' &&
    '__slotId' in child.type &&
    (child.type as SlottableComponent).__slotId === SLOTTABLE_IDENTIFIER
  )
}

// ---------------------------------------------------------------------------
// Lazy component support
// ---------------------------------------------------------------------------

const REACT_LAZY_TYPE = Symbol.for('react.lazy')
/* eslint-disable @typescript-eslint/no-explicit-any */
const useReact: ((payload: any) => any) | undefined = (React as any)[' use '.trim().toString()]

function isLazyComponent(element: unknown): element is { _payload: Promise<any>; $$typeof: symbol } {
  return (
    element != null &&
    typeof element === 'object' &&
    '$$typeof' in element &&
    (element as any).$$typeof === REACT_LAZY_TYPE &&
    '_payload' in element &&
    typeof (element as any)._payload === 'object' &&
    (element as any)._payload !== null &&
    'then' in (element as any)._payload
  )
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// ---------------------------------------------------------------------------
// mergeProps
// ---------------------------------------------------------------------------

/* eslint-disable @typescript-eslint/no-explicit-any */
function mergeProps(slotProps: Record<string, any>, childProps: Record<string, any>): Record<string, any> {
  const overrideProps = { ...childProps }

  for (const propName in childProps) {
    const slotPropValue = slotProps[propName]
    const childPropValue = childProps[propName]
    const isHandler = /^on[A-Z]/.test(propName)

    if (isHandler) {
      if (slotPropValue && childPropValue) {
        overrideProps[propName] = (...args: unknown[]) => {
          const result = childPropValue(...args)
          slotPropValue(...args)
          return result
        }
      } else if (slotPropValue) {
        overrideProps[propName] = slotPropValue
      }
    } else if (propName === 'style') {
      overrideProps[propName] = { ...slotPropValue, ...childPropValue }
    } else if (propName === 'className') {
      overrideProps[propName] = [slotPropValue, childPropValue].filter(Boolean).join(' ')
    }
  }

  return { ...slotProps, ...overrideProps }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// ---------------------------------------------------------------------------
// getElementRef
// ---------------------------------------------------------------------------

function getElementRef(element: React.ReactElement): React.Ref<unknown> | undefined {
  // React 19 compat: check for ref getter warnings
  /* eslint-disable @typescript-eslint/no-explicit-any */
  let getter = Object.getOwnPropertyDescriptor((element as any).props, 'ref')?.get
  let mayWarn = getter && 'isReactWarning' in getter && (getter as any).isReactWarning
  if (mayWarn) {
    return (element as any).ref
  }

  getter = Object.getOwnPropertyDescriptor(element, 'ref')?.get
  mayWarn = getter && 'isReactWarning' in getter && (getter as any).isReactWarning
  if (mayWarn) {
    return (element as any).props.ref
  }

  return (element as any).props.ref || (element as any).ref
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

// ---------------------------------------------------------------------------
// SlotClone (internal)
// ---------------------------------------------------------------------------

interface SlotProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode
}

const SlotClone = React.forwardRef<HTMLElement, SlotProps>((props, forwardedRef) => {
  let { children, ...slotProps } = props

  if (isLazyComponent(children) && typeof useReact === 'function') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children = useReact((children as any)._payload)
  }

  if (React.isValidElement(children)) {
    const childrenRef = getElementRef(children)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const merged = mergeProps(slotProps, (children as any).props)
    if (children.type !== React.Fragment) {
      merged.ref = forwardedRef ? composeRefs(forwardedRef, childrenRef) : childrenRef
    }
    return React.cloneElement(children, merged)
  }

  return React.Children.count(children) > 1 ? React.Children.only(null) : null
})
SlotClone.displayName = 'SlotClone'

// ---------------------------------------------------------------------------
// Slot component
// ---------------------------------------------------------------------------

const SlotComponent = React.forwardRef<HTMLElement, SlotProps>((props, forwardedRef) => {
  let { children, ...slotProps } = props

  if (isLazyComponent(children) && typeof useReact === 'function') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children = useReact((children as any)._payload)
  }

  const childrenArray = React.Children.toArray(children)
  const slottable = childrenArray.find(isSlottable)

  if (slottable) {
    const newElement = (slottable as React.ReactElement<SlottableProps>).props.children
    const newChildren = childrenArray.map((child) => {
      if (child === slottable) {
        if (React.Children.count(newElement) > 1) return React.Children.only(null)
        return React.isValidElement(newElement)
          ? (newElement as React.ReactElement<{ children?: React.ReactNode }>).props.children
          : null
      } else {
        return child
      }
    })

    return (
      <SlotClone {...slotProps} ref={forwardedRef}>
        {React.isValidElement(newElement) ? React.cloneElement(newElement, undefined, newChildren) : null}
      </SlotClone>
    )
  }

  return (
    <SlotClone {...slotProps} ref={forwardedRef}>
      {children}
    </SlotClone>
  )
})
SlotComponent.displayName = 'Slot'

// ---------------------------------------------------------------------------
// Exports
//
// The radix-ui barrel exports Slot as a namespace with { Root, Slot, Slottable, ... }.
// Our 6 target files all use `Slot.Root`. We re-create that shape here so the
// consumer import `import { Slot } from '@/registry/reapit/lib/slot'` works
// and `Slot.Root` resolves to the component.
// ---------------------------------------------------------------------------

/**
 * Slot namespace — mirrors the `radix-ui` barrel export shape.
 *
 * Usage:
 *   import { Slot } from '@/registry/reapit/lib/slot'
 *   <Slot.Root ...>
 */
const Slot = {
  Root: SlotComponent,
  Slot: SlotComponent,
  Slottable,
} as const

export { Slot, SlotComponent, Slottable }
export type { SlotProps }
